import { Mic, Square, Trash2, Play, Pause } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { cn, formatDuration } from '@/lib/utils';

interface VoiceRecorderProps {
  onRecordingChange: (blob: Blob | null) => void;
  disabled?: boolean;
}

export function VoiceRecorder({ onRecordingChange, disabled }: VoiceRecorderProps) {
  const {
    isRecording,
    duration,
    timeLeft,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    clearRecording,
  } = useVoiceRecorder();

  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Notify parent when recording changes
  useEffect(() => {
    onRecordingChange(audioBlob);
  }, [audioBlob, onRecordingChange]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleClear = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    clearRecording();
  };

  const progressPercent = (duration / 60) * 100;

  return (
    <div className="space-y-4">
      {/* Recording UI */}
      {!audioBlob && (
        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled}
            className={cn(
              'w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300',
              isRecording
                ? 'bg-destructive animate-pulse-slow'
                : 'bg-primary hover:bg-primary/90 hover:scale-105',
              disabled && 'opacity-50 pointer-events-none'
            )}
          >
            {isRecording ? (
              <Square className="w-10 h-10 text-white" fill="white" />
            ) : (
              <Mic className="w-10 h-10 text-white" />
            )}
          </button>

          {isRecording && (
            <div className="w-full max-w-xs space-y-2 animate-in">
              <Progress value={progressPercent} />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Recording...</span>
                <span className={cn(
                  'font-mono',
                  timeLeft <= 10 && 'text-destructive font-bold'
                )}>
                  {formatDuration(timeLeft)}
                </span>
              </div>
            </div>
          )}

          {!isRecording && (
            <p className="text-sm text-muted-foreground">
              Tap to record (max 60 seconds)
            </p>
          )}
        </div>
      )}

      {/* Playback UI */}
      {audioBlob && audioUrl && (
        <div className="flex items-center gap-3 p-4 rounded-xl glass animate-in">
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />

          <Button
            type="button"
            variant="secondary"
            size="icon"
            onClick={handlePlayPause}
            disabled={disabled}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>

          <div className="flex-1">
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: isPlaying ? '100%' : '0%' }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatDuration(duration)} recorded
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            disabled={disabled}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

